import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "./auth-context";
import { authHeaders, getSessionToken } from "../lib/api-client";
import { resolveApiUrl } from "../lib/api-origin";

type SubscriptionsByUser = Record<string, string[]>;

/**
 * "loaded" means the list is authoritative. "error" means we do not know what
 * the user owns — callers must not read the empty list as "owns nothing".
 */
export type SubscriptionsStatus = "pending" | "loaded" | "error";

interface SubscriptionsContextValue {
  subscriptions: string[];
  subscriptionsReady: boolean;
  subscriptionsStatus: SubscriptionsStatus;
  hasProduct: (productId: string) => boolean;
  addProduct: (productId: string, targetKey?: string) => void;
  removeProduct: (productId: string) => void;
  migrateUserEmailData: (fromKey: string, toKey: string) => void;
}

const SubscriptionsContext = createContext<SubscriptionsContextValue | undefined>(undefined);

async function loadSubscriptionsFromApi() {
  const response = await fetch("/api/website/subscriptions", {
    headers: authHeaders(),
  });

  if (!response.ok) {
    return null;
  }

  const payload = (await response.json()) as {
    ok: boolean;
    products: string[];
  };

  if (!payload.ok) {
    return null;
  }

  return payload.products;
}

export function SubscriptionsProvider({ children }: { children: ReactNode }) {
  const { auth } = useAuth();
  const [subscriptionsByUser, setSubscriptionsByUser] = useState<SubscriptionsByUser>({});
  const [statusByUser, setStatusByUser] = useState<Record<string, SubscriptionsStatus>>({});

  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    const handler = () => setRefreshCounter((c) => c + 1);
    window.addEventListener("cf:subscriptions-changed", handler);
    window.addEventListener("cf:auth-changed", handler);
    return () => {
      window.removeEventListener("cf:subscriptions-changed", handler);
      window.removeEventListener("cf:auth-changed", handler);
    };
  }, []);

  useEffect(() => {
    if (!auth?.username) {
      return;
    }

    const userKey = auth.username.toLowerCase();

    void (async () => {
      // Checked inside the async body so the state write is never synchronous
      // with the effect. A missing token used to return silently, leaving this
      // user pending forever and the product dashboard spinning.
      if (!getSessionToken()) {
        setStatusByUser((current) => ({ ...current, [userKey]: "error" }));
        return;
      }
      try {
        const products = await loadSubscriptionsFromApi();
        if (products) {
          setSubscriptionsByUser((current) => ({
            ...current,
            [userKey]: products,
          }));
          setStatusByUser((current) => ({ ...current, [userKey]: "loaded" }));
          return;
        }
        // A rejected or unauthorized read is not an empty entitlement list.
        setStatusByUser((current) => ({ ...current, [userKey]: "error" }));
      } catch {
        setStatusByUser((current) => ({ ...current, [userKey]: "error" }));
      }
    })();
  }, [auth?.username, refreshCounter]);

  useEffect(() => {
    if (!auth?.username) return;

    const token = getSessionToken();
    if (!token) return;

    const es = new EventSource(
      resolveApiUrl(`/api/website/payments/stream?sessionToken=${encodeURIComponent(token)}`)
    );

    es.onmessage = (_e) => {
      try {
        // Notify existing listeners to refresh subscriptions
        window.dispatchEvent(new Event("cf:subscriptions-changed"));
      } catch {
        // Ignore
      }
    };

    es.onerror = () => {
      // Do nothing; the client will retry automatically.
    };

    return () => {
      try {
        es.close();
      } catch {
        /* ignore */
      }
    };
  }, [auth?.username]);

  const value = useMemo<SubscriptionsContextValue>(() => {
    const userKey = auth?.username.toLowerCase();
    const subscriptions = userKey ? (subscriptionsByUser[userKey] ?? []) : [];
    const subscriptionsStatus: SubscriptionsStatus = userKey
      ? (statusByUser[userKey] ?? "pending")
      : "loaded";
    const subscriptionsReady = subscriptionsStatus !== "pending";

    return {
      subscriptions,
      subscriptionsReady,
      subscriptionsStatus,
      hasProduct: (productId) => subscriptions.includes(productId),
      addProduct: (productId, targetKey) => {
        const resolvedKey = targetKey?.toLowerCase() ?? userKey;
        if (!resolvedKey) {
          return;
        }

        setSubscriptionsByUser((current) => {
          const nextForUser = Array.from(new Set([...(current[resolvedKey] ?? []), productId]));
          return {
            ...current,
            [resolvedKey]: nextForUser,
          };
        });
      },
      removeProduct: (productId) => {
        if (!userKey) return;
        setSubscriptionsByUser((current) => ({
          ...current,
          [userKey]: (current[userKey] ?? []).filter((id) => id !== productId),
        }));
      },
      migrateUserEmailData: (fromKey, toKey) => {
        const sourceKey = fromKey.trim().toLowerCase();
        const targetKey = toKey.trim().toLowerCase();
        if (!sourceKey || !targetKey || sourceKey === targetKey) {
          return;
        }

        setSubscriptionsByUser((current) => {
          const sourceSubscriptions = current[sourceKey] ?? [];
          const targetSubscriptions = current[targetKey] ?? [];
          const merged = Array.from(new Set([...targetSubscriptions, ...sourceSubscriptions]));
          const next = { ...current, [targetKey]: merged };
          delete next[sourceKey];
          return next;
        });
      },
    };
  }, [auth?.username, statusByUser, subscriptionsByUser]);

  return <SubscriptionsContext.Provider value={value}>{children}</SubscriptionsContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSubscriptions() {
  const context = useContext(SubscriptionsContext);
  if (!context) {
    throw new Error("useSubscriptions must be used within a SubscriptionsProvider");
  }

  return context;
}
